import type {
  ChartType,
  MarketValue,
  Portfolio,
  PortfolioData,
  PortfolioForm,
  PortfolioHolding,
} from "~types/portfolio";
import type { WatchlistPerformanceChartData } from "~types/types";

export const NEW_PORTFOLIO_TAB_TITLE = "New List";

export const SHOW_CHARTS_INITIAL_STATE_STORAGE_KEY = "WDSCIS";

export const NO_COMPARISON_TICKER = `NO_COMPARISON`;

export const CLIENT_FEATURE_FLAG = {
  WATCH_DIS_V1_WITH_LOTS: "W28C43ISV1LOTS",
  WATCH_DIS_V2: "W28C43ISV2",
  TARIFF_BUNDLES: "tariff",
  WATCH_IMPORT_EXPORT: "W28C4IM3X",
  WATCHLIST_DELTA: "WATCHLIST_DELTA",
};

export const CHART_TYPE_FIELD_MAP: {
  [key in ChartType]: keyof Omit<
    WatchlistPerformanceChartData,
    "id" | "name" | "countryCode" | "exchangeCode"
  >;
} = {
  "1D": "priceMovements1Day",
  "1M": "priceMovements1Month",
  "6M": "priceMovements6Months",
  YTD: "priceMovementsYearToDate",
  "1Y": "priceMovements1Year",
  "5Y": "priceMovements5Years",
};

const MINUTE_IN_MILLISECONDS = 60 * 1000;
const DAY_IN_MILLISECONDS = 24 * 60 * MINUTE_IN_MILLISECONDS;
const WEEK_IN_MILLISECONDS = 7 * DAY_IN_MILLISECONDS;

export const CHART_TYPE_TIME_GAP_MAP: {
  [key: string]: number;
} = {
  priceMovements1Day: MINUTE_IN_MILLISECONDS,
  priceMovements1Month: DAY_IN_MILLISECONDS,
  priceMovements6Months: DAY_IN_MILLISECONDS,
  priceMovementsYearToDate: DAY_IN_MILLISECONDS,
  priceMovements1Year: DAY_IN_MILLISECONDS,
  priceMovements5Years: WEEK_IN_MILLISECONDS,
};

export const WATCHLIST_FAILURE_TEXT = [
  "There was a network error.",
  "Please try again.",
];

export const MAX_WATCHLIST_LIMIT = {
  registered: 10,
  subscribed: 100,
};
export const WATCHLIST_DELETE_FAILURE_TEXT =
  "Unable to delete your watchlist due to a network error. Please try again.";

export const WATCHLIST_SAVE_FAILURE_TEXT =
  "Unable to save your watchlist due to a network error. Please try again.";

export const WATCHLIST_NEWS_FAILURE_TEXT =
  "We’re sorry, there’s been an issue loading your news.";

export const DEFAULT_PORTFOLIO_LOT: PortfolioHolding = {
  lotId: -1,
  shares: "0",
  buyPrice: "0",
  buyDate: -1,
};

export const DEFAULT_PORTFOLIO_FORM: PortfolioForm = {
  currency: "USD",
  holdings: [],
  isDefault: false,
  name: "",
};

export const DEFAULT_PORTFOLIO: Portfolio = {
  marketValue: {} as MarketValue,
  portInfo: {
    currency: "USD",
    id: -1,
    isDefault: false,
    name: "My Watchlist",
    timestamp: {
      createdAt: -1,
      updatedAt: -1,
    },
  },
  positions: [],
};

export const DEFAULT_PORTFOLIO_DATA: PortfolioData = {
  port: DEFAULT_PORTFOLIO,
  portInfos: [DEFAULT_PORTFOLIO.portInfo],
};

export const ROW_TYPES = {
  TICKER: "ticker",
  LOT: "lot",
};

export const LOADING_STATE = {
  INITIAL: "initial",
  LOADING: "loading",
  COMPLETE: "complete",
  FAILED: "failed",
  EMPTY: "empty",
};

export const ENTRY_DATA_FORMAT = {
  PRICE_CHANGE: "formatPriceChange",
  PERCENT_CHANGE: "formatPercentChange",
  COMMAS_AND_DECIMALS: "formatWithCommasAndDecimals",
  BIG_NUMBER: "formatBigNumber",
};

export const MAX_WATCHLISTS = {
  REGISTERED: 10,
  SUBSCRIBED: 100,
};
export const MAX_TICKERS_PER_WATCHLIST = {
  REGISTERED: 25,
  SUBSCRIBED: 100,
};

export const MAX_NEWS_FEED = 20;
export const SORT_STATES = {
  NEUTRAL: "neutral",
  INCREASING: "increasing",
  DECREASING: "decreasing",
};

export const FORM_ACTION = {
  CANCEL: "cancel",
  DELETE: "delete",
  SAVE: "save",
  DELETE_PERMANENTLY: "delete_permanently",
};

export const PORTFOLIO_TABLE_COLUMNS_CONFIG = [
  {
    columnName: "SYMBOL",
    columnAriaLabel: "",
    alignment: "left",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "Lot last updated",
    tickerPrimaryLodashPath: "security.ticker",
    tickerPrimaryURL: (quote: string) => `/quote/${quote}`,
    tickerSecondaryLodashPath: "security.LONG_COMP_NAME",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "shares.buydate",
    primaryDataFormat: "",
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "LOTS",
    columnAriaLabel: "",
    alignment: "left",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "lots",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "shares.number",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "shares.number",
    primaryDataFormat: "",
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "PRICE",
    columnAriaLabel: "",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "Security last updated",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.PX_LAST",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketData.LAST_UPDATE",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "shares.buyprice",
    primaryDataFormat: ENTRY_DATA_FORMAT.COMMAS_AND_DECIMALS,
    secondaryDataFormat: "",
    lotDataFormat: ENTRY_DATA_FORMAT.COMMAS_AND_DECIMALS,
    enableSort: true,
  },
  {
    columnName: "CHANGE",
    columnAriaLabel: "one day change",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.CHG_PCT_1D",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketData.CHG_NET_1D",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "",
    primaryDataFormat: ENTRY_DATA_FORMAT.PERCENT_CHANGE,
    secondaryDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    lotDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    enableSort: true,
  },
  {
    columnName: "TOTAL GAIN",
    columnAriaLabel: "",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketValue.totalGainLoss.value",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketValue.totalGainLoss.pctChange",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "marketValue.totalGainLoss.value",
    primaryDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    secondaryDataFormat: ENTRY_DATA_FORMAT.PERCENT_CHANGE,
    lotDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    enableSort: true,
  },
  {
    columnName: "1D GAIN",
    columnAriaLabel: "one day gain",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketValue.todayGainLoss.value",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketValue.todayGainLoss.pctChange",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "marketValue.todayGainLoss.value",
    primaryDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    secondaryDataFormat: ENTRY_DATA_FORMAT.PERCENT_CHANGE,
    lotDataFormat: ENTRY_DATA_FORMAT.PRICE_CHANGE,
    enableSort: true,
  },
  {
    columnName: "OPEN",
    columnAriaLabel: "open price",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.PX_OPEN",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "",
    primaryDataFormat: ENTRY_DATA_FORMAT.BIG_NUMBER,
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "PREV CLOSE",
    columnAriaLabel: "previous close",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.PX_CLOSE_1D",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "",
    primaryDataFormat: ENTRY_DATA_FORMAT.BIG_NUMBER,
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "VOLUME",
    columnAriaLabel: "volume count",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.PX_VOLUME",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "",
    primaryDataFormat: ENTRY_DATA_FORMAT.COMMAS_AND_DECIMALS,
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "MARKET CAP",
    columnAriaLabel: "",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.CUR_MKT_CAP",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "",
    tickerTertiaryLodashPath: "",
    lotLodashPath: "",
    primaryDataFormat: ENTRY_DATA_FORMAT.BIG_NUMBER,
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: true,
  },
  {
    columnName: "DAY RANGE",
    columnAriaLabel: "",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.PX_HIGH",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketData.PX_LOW",
    tickerTertiaryLodashPath: "marketData.PX_LAST",
    lotLodashPath: "",
    primaryDataFormat: "",
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: false,
  },
  {
    columnName: "52WK RANGE",
    columnAriaLabel: "52 week range",
    alignment: "right",
    tickerPrimaryAriaLabel: "",
    tickerSecondaryAriaLabel: "",
    lotAriaLabel: "",
    tickerPrimaryLodashPath: "marketData.HIGH_52WEEK",
    tickerPrimaryURL: null,
    tickerSecondaryLodashPath: "marketData.LOW_52WEEK",
    tickerTertiaryLodashPath: "marketData.PX_LAST",
    lotLodashPath: "",
    primaryDataFormat: "",
    secondaryDataFormat: "",
    lotDataFormat: "",
    enableSort: false,
  },
];

export const CRYPTO_BUNDLE_SECURITIES = [
  {
    ticker: "XBT:CUR",
    name: "Bitcoin",
  },
  {
    ticker: "XET:CUR",
    name: "Ethereum",
  },
  {
    ticker: "XLC:CUR",
    name: "Litecoin",
  },
  {
    ticker: "XTH:CUR",
    name: "Ethereum Classic",
  },
  {
    ticker: "XEO:CUR",
    name: "EOS",
  },
  {
    ticker: "XDH:CUR",
    name: "Dash",
  },
  {
    ticker: "XBN:CUR",
    name: "Bitcoin Cash",
  },
  {
    ticker: "CRON:US",
    name: "Cronos Group",
  },
  {
    ticker: "LINK:SW",
    name: "Chainlink",
  },
  {
    ticker: "AXRP:SW",
    name: "21Shares XRP ETP",
  },
];

export const TRENDING_BUNDLE_SECURITIES = [
  {
    ticker: "AMZN:US",
    name: "Amazon",
  },
  {
    ticker: "TSLA:US",
    name: "Tesla",
  },
  {
    ticker: "AAPL:US",
    name: "Apple",
  },
  {
    ticker: "NVDA:US",
    name: "Nvidia",
  },
  {
    ticker: "SOFI:US",
    name: "SoFi Technologies",
  },
  {
    ticker: "AMD:US",
    name: "Advanced Micro Devices",
  },
  {
    ticker: "PYPL:US",
    name: "PayPal",
  },
  {
    ticker: "MSFT:US",
    name: "Microsoft",
  },
  {
    ticker: "META:US",
    name: "Meta Platforms",
  },
  {
    ticker: "GOOGL:US",
    name: "Alphabet Inc.",
  },
];

export const GLOBAL_MARKETS_BUNDLE_SECURITIES = [
  {
    ticker: "SPX:IND",
    name: "S&P 500",
  },
  {
    ticker: "INDU:IND",
    name: "Dow Jones",
  },
  {
    ticker: "CCMP:IND",
    name: "Nasdaq Composite",
  },
  {
    ticker: "NYA:IND",
    name: "NYSE Composite",
  },
  {
    ticker: "UKX:IND",
    name: "FTSE 100",
  },
  {
    ticker: "DAX:IND",
    name: "Dax Performance Index",
  },
  {
    ticker: "NKY:IND",
    name: "Nikkei 225",
  },
  {
    ticker: "HSI:IND",
    name: "Hang Seng Index",
  },
  {
    ticker: "TPX:IND",
    name: "Tokyo Stock Exchange",
  },
  {
    ticker: "BDIY:IND",
    name: "Baltic Exchange",
  },
];

export const AUTO_BUNDLE_SECURITIES = [
  {
    ticker: "F:US",
    name: "Ford Motor",
  },
  {
    ticker: "RACE:US",
    name: "Ferrari NV",
  },
  {
    ticker: "GM:US",
    name: "General Motors",
  },
  {
    ticker: "TSLA:US",
    name: "Tesla",
  },
  {
    ticker: "STLA:US",
    name: "Stellantis NV",
  },
  {
    ticker: "PCAR:US",
    name: "PACCAR",
  },
  {
    ticker: "CMI:US",
    name: "Cummins",
  },
  {
    ticker: "LCID:US",
    name: "Lucid Group",
  },
  {
    ticker: "LI:US",
    name: "Li Auto",
  },
  {
    ticker: "RIVN:US",
    name: "Rivian Automotive",
  },
];

export const RETAIL_BUNDLE_SECURITIES = [
  {
    ticker: "WMT:US",
    name: "Walmart",
  },
  {
    ticker: "COST:US",
    name: "Costco Wholesale",
  },
  {
    ticker: "HD:US",
    name: "Home Depot",
  },
  {
    ticker: "TJX:US",
    name: "TJX Cos",
  },
  {
    ticker: "LOW:US",
    name: "Lowe's Cos",
  },
  {
    ticker: "TGT:US",
    name: "Target",
  },
  {
    ticker: "DG:US",
    name: "Dollar General",
  },
  {
    ticker: "DLTR:US",
    name: "Dollar Tree",
  },
  {
    ticker: "ROST:US",
    name: "Ross Stores",
  },
  {
    ticker: "TPR:US",
    name: "Tapestry",
  },
];

export const SEMICONDUCTORS_BUNDLE_SECURITIES = [
  {
    ticker: "NVDA:US",
    name: "NVIDIA",
  },
  {
    ticker: "AVGO:US",
    name: "Broadcom",
  },
  {
    ticker: "TSM:US",
    name: "Taiwan Semiconductor Manufacturing",
  },
  {
    ticker: "ASML:US",
    name: "ASML Holding NV",
  },
  {
    ticker: "AMD:US",
    name: "Advanced Micro Devices",
  },
  {
    ticker: "QCOM:US",
    name: "QUALCOMM",
  },
  {
    ticker: "TXN:US",
    name: "Texas Instruments",
  },
  {
    ticker: "ARM:US",
    name: "ARM Holdings PLC",
  },
  {
    ticker: "INTC:US",
    name: "Intel",
  },
  {
    ticker: "AMAT:US",
    name: "Applied Materials",
  },
];

export const CRITICAL_MINERALS_BUNDLE_SECURITIES = [
  {
    ticker: "600111:CH",
    name: "China Northern Rare Earth Group High-Tech",
  },
  {
    ticker: "LYC:AU",
    name: "Lynas Rare Earths",
  },
  {
    ticker: "ALB:US",
    name: "Albemarle",
  },
  {
    ticker: "SQM:US",
    name: "Sociedad Quimica y Minera de Chile SA",
  },
  {
    ticker: "MP:US",
    name: "MP Materials",
  },
  {
    ticker: "PLS:AU",
    name: "Pilbara Minerals",
  },
  {
    ticker: "601958:CH",
    name: "Jinduicheng Molybdenum",
  },
  {
    ticker: "AMG:NA",
    name: "AMG Critical Materials NV",
  },
  {
    ticker: "LAC:CN",
    name: "Lithium Americas",
  },
  {
    ticker: "600456:CH",
    name: "Baoji Titanium Industry",
  },
];

export const DASHBOARD_v2_FEEDBACK_URL =
  "https://bmedia.iad1.qualtrics.com/jfe/form/SV_6xOBCxI5WTQX3bU";
